<template>
    <div v-if="todisplay"
         v-show="isvisible"
         role="group"
         aria-label="Search query builder"
         :style="[styles, containerStyle]"
         :class="['c-SearchQueryBuilder', `c-name-${name || 'unnamed'}`]">
        <div v-if="rootGroup" class="search-query-builder-root">
            <div class="row items-center q-mb-sm">
                <span class="q-mr-sm">Match</span>
                <q-select v-model="rootGroup.operator"
                          :options="operatorOptions"
                          dense
                          outlined
                          hide-bottom-space
                          emit-value
                          map-options
                          options-dense
                          class="search-query-root-operator"
                          style="min-width: 90px;"
                          aria-label="Root group operator" />
                <span class="q-ml-sm">of the following:</span>
            </div>
            <div class="search-query-builder-body">
                <Container v-if="rootGroup.children && rootGroup.children.length > 0"
                           class="search-query-container"
                           :group-name="smoothDndGroupName"
                           :get-child-payload="getRootChildPayload"
                           :get-ghost-parent="getGhostParent"
                           :drag-handle-selector="'.column-drag-handle'"
                           @drop="onRootDrop">
                    <Draggable v-for="(child, index) in rootGroup.children"
                               :key="child.id"
                               class="search-query-draggable">
                        <!-- Root-level term row: Field + Operator + Value by type -->
                        <div v-if="child.type === 'term'"
                             class="search-query-term-row row items-center q-mb-sm">
                            <span class="search-query-drag-handle column-drag-handle"
                                  aria-label="Drag to reorder">
                                <q-icon name="mdi-drag" size="xs" />
                            </span>
                            <q-select v-model="child.field"
                                      :options="filteredFieldOptions"
                                      use-input
                                      fill-input
                                      hide-selected
                                      option-label="label"
                                      dense
                                      outlined
                                      hide-bottom-space
                                      placeholder="Select field"
                                      class="search-query-field-select"
                                      style="min-width: 110px;"
                                      aria-label="Field"
                                      @update:model-value="(v) => onRootFieldChange(child, v)"
                                      @filter="filterFields" />
                            <q-select v-if="child.field && getOperatorOptionsForType(child.field?.type).length > 0"
                                      v-model="child.operator"
                                      :options="getOperatorOptionsForType(child.field?.type)"
                                      dense
                                      outlined
                                      hide-bottom-space
                                      emit-value
                                      map-options
                                      options-dense
                                      class="search-query-operator-term"
                                      style="min-width: 95px;"
                                      aria-label="Operator"
                                      @update:model-value="(v) => onRootOperatorChange(child, v)" />
                            <template v-if="child.field && child.field?.type === 'text'">
                                <q-input v-model="child.value"
                                         dense
                                         outlined
                                         hide-bottom-space
                                         placeholder="Value"
                                         class="col search-query-value-input"
                                         aria-label="Value" />
                                <q-checkbox v-model="child.caseInsensitive"
                                            dense
                                            class="q-ml-sm"
                                            aria-label="Case insensitive">
                                    <q-tooltip>Case Insensitive</q-tooltip>
                                </q-checkbox>
                            </template>
                            <template v-else-if="child.field && child.field?.type === 'numeric' && child.operator !== 'range'">
                                <q-input v-model.number="child.value"
                                         type="number"
                                         dense
                                         outlined
                                         hide-bottom-space
                                         placeholder="Value"
                                         class="search-query-value-input"
                                         style="min-width: 72px;"
                                         aria-label="Value" />
                            </template>
                            <template v-else-if="child.field && child.field?.type === 'numeric' && child.operator === 'range'">
                                <q-input :model-value="Array.isArray(child.value) ? child.value[0] : null"
                                         type="number"
                                         dense
                                         outlined
                                         hide-bottom-space
                                         placeholder="Min"
                                         class="search-query-value-input"
                                         style="min-width: 68px;"
                                         aria-label="Min"
                                         @update:model-value="(v) => setRootRangeValue(child, 0, v)" />
                                <q-input :model-value="Array.isArray(child.value) ? child.value[1] : null"
                                         type="number"
                                         dense
                                         outlined
                                         hide-bottom-space
                                         placeholder="Max"
                                         class="search-query-value-input"
                                         style="min-width: 68px;"
                                         aria-label="Max"
                                         @update:model-value="(v) => setRootRangeValue(child, 1, v)" />
                            </template>
                            <template v-else-if="child.field && child.field?.type === 'date' && child.operator !== 'range'">
                                <q-input v-model="child.value"
                                         type="date"
                                         dense
                                         outlined
                                         hide-bottom-space
                                         class="search-query-value-input"
                                         style="min-width: 110px;"
                                         aria-label="Value" />
                            </template>
                            <template v-else-if="child.field && child.field?.type === 'date' && child.operator === 'range'">
                                <q-input :model-value="Array.isArray(child.value) ? child.value[0] : ''"
                                         type="date"
                                         dense
                                         outlined
                                         hide-bottom-space
                                         placeholder="From"
                                         class="search-query-value-input"
                                         style="min-width: 100px;"
                                         aria-label="From"
                                         @update:model-value="(v) => setRootRangeValue(child, 0, v)" />
                                <q-input :model-value="Array.isArray(child.value) ? child.value[1] : ''"
                                         type="date"
                                         dense
                                         outlined
                                         hide-bottom-space
                                         placeholder="To"
                                         class="search-query-value-input"
                                         style="min-width: 100px;"
                                         aria-label="To"
                                         @update:model-value="(v) => setRootRangeValue(child, 1, v)" />
                            </template>
                            <template v-else-if="child.field && child.field?.type === 'boolean'">
                                <q-select v-model="child.value"
                                          :options="booleanValueOptions"
                                          dense
                                          outlined
                                          hide-bottom-space
                                          emit-value
                                          map-options
                                          options-dense
                                          class="search-query-value-input"
                                          style="min-width: 80px;"
                                          aria-label="Value" />
                            </template>
                            <q-btn flat
                                   round
                                   dense
                                   size="sm"
                                   icon="mdi-close"
                                   aria-label="Remove term"
                                   @click="removeRootChild(index)">
                                <q-tooltip>Remove term</q-tooltip>
                            </q-btn>
                        </div>
                        <!-- Root-level nested group -->
                        <SearchQueryGroup v-else-if="child.type === 'group'"
                                          :group="child"
                                          :depth="1"
                                          :parent-children="rootGroup.children"
                                          :self-index="index"
                                          :root-operator="rootGroup.operator"
                                          @change="onModelChange" />
                        <div v-if="index < rootGroup.children.length - 1"
                             class="search-query-operator-separator">
                            {{ rootGroup.operator === 'or' ? '- OR -' : '- AND -' }}
                        </div>
                    </Draggable>
                </Container>
            </div>

            <div v-if="!(rootGroup.children && rootGroup.children.length > 0)" class="search-query-empty-hint q-mb-sm text-grey-7">
                No terms yet. Add a term or group below.
            </div>

            <div class="row q-gutter-sm q-mt-sm">
                <q-btn size="sm"
                       icon="mdi-plus"
                       color="primary"
                       outline
                       aria-label="Add term"
                       @click="addRootTerm">
                    <q-tooltip>Add term</q-tooltip>
                    Add term
                </q-btn>
                <q-btn size="sm"
                       icon="mdi-plus"
                       color="primary"
                       outline
                       aria-label="Add group"
                       @click="addRootGroup">
                    <q-tooltip>Add group</q-tooltip>
                    Add group
                </q-btn>
            </div>
        </div>
    </div>
</template>

<script>
    import utils from '../../Shared/utils.jsx';
    import BaseComponent from './BaseComponentMixin.jsx';
    import { Container, Draggable } from 'vue3-smooth-dnd';
    import { QInput, QSelect, QCheckbox, QBtn, QIcon, QTooltip } from 'quasar';
    import SearchQueryGroup from './vuecontrols/SearchQueryGroup.vue';
    import {
        SMOOTH_DND_GROUP_NAME,
        MOCK_FIELD_OPTIONS,
        BOOLEAN_VALUE_OPTIONS,
        getOperatorOptionsForType as getOperatorOptions,
        getDefaultOperatorForType,
        getDefaultValueForTerm,
    } from './vuecontrols/searchQueryBuilderFields.js';

    function createTerm() {
      return {
        type: 'term',
        field: null,
        operator: 'partial',
        value: '',
        caseInsensitive: false,
        id: utils.generateUUID(),
      };
    }

    function createGroup() {
      return {
        type: 'group',
        operator: 'and',
        children: [],
        id: utils.generateUUID(),
      };
    }

    function createDefaultRootGroup() {
      return createGroup();
    }

    function isValidRootGroup(value) {
      if (!value || typeof value !== 'object') return false;
      if (value.type !== 'group') return false;
      if (!Array.isArray(value.children)) return false;
      return true;
    }

    function ensureNodeId(node) {
      if (!node.id) node.id = utils.generateUUID();
      if (node.type === 'group' && Array.isArray(node.children)) {
        node.children.forEach(ensureNodeId);
      }
    }

    function normalizeRootGroup(value) {
      const root = { ...value, children: value.children ? [...value.children] : [] };
      ensureNodeId(root);
      return root;
    }

    export default {
        name: 'search-query-builder',
        mixins: [BaseComponent],
        components: {
            Container,
            Draggable,
            QInput,
            QSelect,
            QCheckbox,
            QBtn,
            QIcon,
            QTooltip,
            SearchQueryGroup,
        },
        data() {
            return {
                rootGroup: createDefaultRootGroup(),
                modelExpn: null,
                modelExpnIsObject: false,
                fieldOptions: MOCK_FIELD_OPTIONS,
                filteredFieldOptions: [...MOCK_FIELD_OPTIONS],
                booleanValueOptions: BOOLEAN_VALUE_OPTIONS,
                operatorOptions: [
                    { value: 'and', label: 'All' },
                    { value: 'or', label: 'Any' },
                ],
            };
        },
        computed: {
            Model() {
                return this.rootGroup;
            },
            styles() {
                return {
                    ...utils.resolveStyleHints(this.styleHints, this),
                    ...this.sizeStyle,
                }
            },
            containerStyle() {
                return {
                    maxWidth: '750px',
                    overflowY: 'auto',
                };
            },
            smoothDndGroupName() {
                return SMOOTH_DND_GROUP_NAME;
            },
        },
        created() {
            const initialProp = this.controlData?.Model ?? this.controlData?.InitialModel;
            if (initialProp == null) return;
            if (typeof initialProp === 'object' && isValidRootGroup(initialProp)) {
                this.rootGroup = normalizeRootGroup(initialProp);
                return;
            }
            if (typeof initialProp !== 'string' || initialProp === '') return;
            try {
                if (initialProp.startsWith('{#') || initialProp.startsWith('{%')) {
                    this.modelExpn = utils.compileObject(this, initialProp);
                    this.modelExpnIsObject = true;
                } else {
                    this.modelExpn = utils.compile(this, initialProp);
                    this.modelExpnIsObject = false;
                }
            } catch (e) {
                utils.warn('SearchQueryBuilder: failed to compile initial Model expression', e);
                return;
            }
            if (this.modelExpn) {
                try {
                    const evaluated = this.modelExpnIsObject
                        ? utils.evaluateObject(this.modelExpn, this)
                        : utils.evaluate(this.modelExpn, this);
                    if (isValidRootGroup(evaluated)) {
                        this.rootGroup = normalizeRootGroup(evaluated);
                    }
                } catch (e) {
                    utils.warn('SearchQueryBuilder: failed to evaluate initial Model', e);
                }
            }
        },
        methods: {
            preRenderComplete() {
                this.finishRenderHandler(this);
            },
            getRootChildPayload(index) {
                return this.rootGroup.children && this.rootGroup.children[index];
            },
            getGhostParent() {
                return typeof document !== 'undefined' ? document.body : null;
            },
            onRootDrop(result) {
                if (result.removedIndex == null && result.addedIndex == null) return;
                if (!this.rootGroup.children) this.rootGroup.children = [];
                const children = this.rootGroup.children;
                let itemToAdd = result.payload;
                if (result.removedIndex !== null) {
                    itemToAdd = children.splice(result.removedIndex, 1)[0];
                }
                if (result.addedIndex !== null && itemToAdd != null) {
                    children.splice(result.addedIndex, 0, itemToAdd);
                }
            },
            removeRootChild(index) {
                this.rootGroup.children.splice(index, 1);
            },
            addRootTerm() {
                if (!this.rootGroup.children) this.rootGroup.children = [];
                this.rootGroup.children.push(createTerm());
            },
            addRootGroup() {
                if (!this.rootGroup.children) this.rootGroup.children = [];
                this.rootGroup.children.push(createGroup());
            },
            onModelChange() {
                this.$forceUpdate?.();
            },
            getOperatorOptionsForType(fieldType) {
                return getOperatorOptions(fieldType);
            },
            filterFields(val, update) {
                update(() => {
                    if (val === '') {
                        this.filteredFieldOptions = [...this.fieldOptions];
                    } else {
                        const needle = val.toLowerCase();
                        this.filteredFieldOptions = this.fieldOptions.filter(
                            (f) => f.label.toLowerCase().indexOf(needle) > -1
                        );
                    }
                });
            },
            onRootFieldChange(term, field) {
                term.field = field || null;
                if (field) {
                    term.operator = getDefaultOperatorForType(field.type);
                    term.value = getDefaultValueForTerm(field.type, term.operator);
                }
            },
            onRootOperatorChange(term, operator) {
                const prevRange = term.operator === 'range';
                const nextRange = operator === 'range';
                term.operator = operator;
                if (!prevRange && nextRange) {
                    const single = term.value;
                    term.value = term.field?.type === 'date' ? [single || '', single || ''] : [single, single];
                } else if (prevRange && !nextRange) {
                    term.value = Array.isArray(term.value) ? term.value[0] : getDefaultValueForTerm(term.field?.type, operator);
                }
            },
            setRootRangeValue(term, index, v) {
                if (!Array.isArray(term.value)) term.value = [null, null];
                const arr = [...term.value];
                arr[index] = term.field?.type === 'numeric' ? (v === '' || v == null ? null : Number(v)) : v;
                term.value = arr;
            },
        },
    };
</script>

<style scoped>
    .c-SearchQueryBuilder {
        padding: 4px 0;
    }

    .search-query-builder-root {
        width: 100%;
    }

    .search-query-builder-body {
        position: relative;
        padding-left: 20px;
    }

        .search-query-builder-body::before {
            content: '';
            position: absolute;
            left: 8px;
            top: 0;
            bottom: 0;
            width: 1px;
            background: rgba(0, 0, 0, 0.2);
            pointer-events: none;
        }

    .search-query-drag-handle,
    .column-drag-handle {
        cursor: grab;
        padding: 2px 4px;
        user-select: none;
    }

        .search-query-drag-handle:active,
        .column-drag-handle:active {
            cursor: grabbing;
        }

    .search-query-term-row {
        gap: 6px;
        flex-wrap: wrap;
    }

    .search-query-field-select,
    .search-query-operator-term {
        flex-shrink: 0;
    }

    .search-query-value-input {
        min-width: 60px;
        flex: 1;
    }

    .c-SearchQueryBuilder :deep(.q-field) {
        font-size: 0.875rem;
    }

    .c-SearchQueryBuilder :deep(.q-field__control-container) {
        min-height: 32px;
    }

    .c-SearchQueryBuilder :deep(.q-field .q-field__control) {
        min-height: 32px;
    }

    .search-query-container {
        min-height: 4px;
    }

    .search-query-drop-placeholder {
        min-height: 48px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px dashed rgba(0, 0, 0, 0.3);
        border-radius: 8px;
        color: rgba(0, 0, 0, 0.45);
        font-size: 0.9rem;
        padding: 8px;
    }

    .search-query-draggable {
        margin-bottom: 4px;
    }

    .search-query-operator-separator {
        font-size: 0.7rem;
        color: rgba(0, 0, 0, 1);
        text-align: center;
        padding: 2px 0;
        line-height: 1.2;
        font-weight: bold;
    }

    .search-query-empty-hint {
        font-size: 0.85rem;
    }
</style>
